import React from 'react';
import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react';
import { SectionCard } from '../../common/SectionCard';
import { EmptyState } from '../../common/EmptyState';
import { formatAed, formatDate } from '../../../utils/format';
import type { ApprovalDetail } from '../../../types/domain';
const statusIcon = {
  signed: CheckCircle2,
  pending: CircleDashed,
  blocked: XCircle
};
const statusClasses = {
  signed: 'text-uae-leaf',
  pending: 'text-[#8a6f11]',
  blocked: 'text-uae-red'
};
export function ApprovalPanel({
  approval
}: {
  approval?: ApprovalDetail;
}) {
  if (!approval) {
    return <EmptyState title="Approval record not opened" description="The approval record is created when the Bid gate clears and the funding mandate request is lodged." data-id="element-539" data-mp="36hfyjjmny8kd" />;
  }
  return <div className="space-y-4" data-id="element-540" data-mp="2dysiiimixjq5">
      <div className="grid gap-4 lg:grid-cols-3" data-id="element-541" data-mp="2dy0jextpx3v4">
        <SectionCard title="Funding mandate" data-id="element-542" data-mp="291sai26466we">
          <p className="font-mono text-xl font-semibold" data-id="element-543" data-mp="28ywc6cqctg7f">{formatAed(approval.mandateAed)}</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-544" data-mp="2983ej21fzyk8">
            Federal envelope requested for the full lifecycle
          </p>
        </SectionCard>
        <SectionCard title="Committee date" data-id="element-545" data-mp="2iz7plqnzyz9s">
          <p className="text-xl font-semibold" data-id="element-546" data-mp="2j09byragr86t">{formatDate(approval.committeeDate)}</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-547" data-mp="2myymmc44abmr">
            Decision record captured at the session
          </p>
        </SectionCard>
        <SectionCard title="Decision" data-id="element-548" data-mp="1v7sjrnx0hzts">
          <p className="text-xl font-semibold capitalize" data-id="element-549" data-mp="1v8u8gv6yaktz">{approval.decision}</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-550" data-mp="1z9e4twjp9kf2">
            {approval.decision === 'pending' ? 'Awaiting committee decision' : approval.decision === 'conditional' ? 'Approved subject to recorded conditions' : 'Approved with mandate issued'}
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Federal stakeholder alignment" description="Mandatory sign-offs required before the approval gate can clear" bodyClassName="p-0" data-id="element-551" data-mp="1flwenxfuhgtt">
        <ul className="divide-y divide-border" data-id="element-552" data-mp="1pkc6k2mp4u4d">
          {approval.stakeholders.map(stakeholder => {
          const Icon = statusIcon[stakeholder.status];
          return <li key={stakeholder.id} className="flex items-start justify-between gap-3 px-4 py-2.5" data-id="element-553" data-mp="1kjg1vmuit3xw">
                <div className="flex min-w-0 items-start gap-2.5" data-id="element-554" data-mp="rxmu0yudaz49">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${statusClasses[stakeholder.status]}`} aria-hidden="true" data-id="element-555" data-mp="16l7yox6w56ea" />
                  <div className="min-w-0" data-id="element-556" data-mp="11sy8rh5s06c0">
                    <p className="text-[13px] text-foreground" data-id="element-557" data-mp="e45ahe1olme8">{stakeholder.body}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-558" data-mp="96lyylwdbnpz">
                      {stakeholder.representative}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 text-[11px] text-muted-foreground" data-id="element-559" data-mp="2pjw4nt3p1uxx">
                  {stakeholder.status === 'signed' && stakeholder.on ? `Signed ${formatDate(stakeholder.on)}` : stakeholder.status === 'pending' ? 'Pending' : 'Blocked'}
                </span>
              </li>;
        })}
        </ul>
      </SectionCard>

      <SectionCard title="Conditions attached" description="Conditions carried forward into the next stage" data-id="element-560" data-mp="38hyxkizd4c10">
        {approval.conditions.length === 0 ? <p className="text-[12px] text-muted-foreground" data-id="element-561" data-mp="2zhtxyv0euhxe">
            No conditions attached to this approval.
          </p> : <ul className="space-y-2" data-id="element-562" data-mp="1j2sfkui7z3ie">
            {approval.conditions.map(condition => <li key={condition} className="rounded-sm border border-border bg-muted/50 px-3 py-2 text-[12px] leading-relaxed" data-id="element-563" data-mp="1o4rk47pp46gx">
                {condition}
              </li>)}
          </ul>}
      </SectionCard>
    </div>;
}